<template>
  <!-- ============================================================
       No.23 アンケート機能：回答の共通コンポーネント（報告時）
       報告者（入力）と管理者（報告管理 Edit・閲覧）で共用。writable=false
       のとき他タブと同様、入力欄を disabled（グレー・変更不可）で表示する。
       外枠は他タブと同じ perfect-scrollbar + ContentsBox。
       ただし複数回アンケートでは回ごとに枠が出ると回が独立した箱に見えてしまうため、
       framed=false で枠と高さ固定を外し、呼び出し側が全回をまとめて1つの枠に入れる。
       ============================================================ -->
  <perfect-scrollbar
    :options="scrollOption"
    :class="framed ? 'py-2 px-2 ContentsBox' : ''">
    <div class="survey-list">
      <div
        v-for="survey in surveys" :key="survey.id"
        class="survey-block mb-4 pb-3">
        <!-- 設問見出し -->
        <div class="survey-question mb-2">
          <span v-if="survey.is_required" class="badge badge-required mr-2">必須</span>
          <span class="q-text">{{ survey.question_text }}</span>
        </div>

        <div class="survey-answer ml-1">
          <!-- フリーテキスト -->
          <template v-if="survey.input_type === TYPE.TEXT">
            <b-form-textarea
              :value="getText(survey.id)"
              @input="val => setText(survey.id, val)"
              :disabled="!writable"
              :maxlength="survey.max_length || null"
              rows="4"
              placeholder="ご記入ください"/>
            <div v-if="writable && survey.max_length" class="text-right text-muted small mt-1">
              {{ getText(survey.id).length }} / {{ survey.max_length }} 文字
            </div>
          </template>

          <!-- ドロップダウン（単一選択） -->
          <template v-else-if="survey.input_type === TYPE.DROPDOWN">
            <b-form-select
              :value="getSingle(survey.id)"
              @change="val => setSingle(survey.id, val)"
              :disabled="!writable"
              class="survey-select">
              <template v-slot:first>
                <b-form-select-option :value="null">-- 選択してください。 --</b-form-select-option>
              </template>
              <b-form-select-option
                v-for="opt in survey.options" :key="opt.id"
                :value="opt.id">{{ opt.label }}</b-form-select-option>
            </b-form-select>
          </template>

          <!-- ラジオボタン（単一選択） -->
          <template v-else-if="survey.input_type === TYPE.RADIO">
            <b-form-radio-group
              :checked="getSingle(survey.id)"
              @input="val => setSingle(survey.id, val)"
              :disabled="!writable"
              class="survey-radio"
              stacked>
              <b-form-radio
                v-for="opt in survey.options" :key="opt.id"
                :value="opt.id"
                class="my-2">{{ opt.label }}</b-form-radio>
            </b-form-radio-group>
          </template>

          <!-- チェックボックス（複数選択） -->
          <template v-else-if="survey.input_type === TYPE.CHECKBOX">
            <b-form-checkbox-group
              :checked="getMulti(survey.id)"
              @input="vals => setMulti(survey.id, vals)"
              :disabled="!writable"
              class="survey-checkbox"
              stacked>
              <b-form-checkbox
                v-for="opt in survey.options" :key="opt.id"
                :value="opt.id"
                class="my-2">{{ opt.label }}</b-form-checkbox>
            </b-form-checkbox-group>
          </template>

          <!-- 必須エラー -->
          <div v-if="errors[survey.id]" class="text-danger small mt-1">
            {{ errors[survey.id] }}
          </div>
        </div>
      </div>
    </div>
  </perfect-scrollbar>
</template>

<script>
import surveyConst from '@/constants/survey';

const TYPE = surveyConst.SURVEY_INPUT_TYPE;

export default {
  name: 'reportSurvey',
  props: {
    // 設問（選択肢込み）：[{id, sort_no, question_text, input_type, max_length, is_required, options:[{id,label}]}]
    surveys: { type: Array, default: () => [] },
    // 回答マップ（v-model）：{ [surveyId]: { answer_text, option_ids } }
    value: { type: Object, default: () => ({}) },
    // 入力可（報告者）／変更不可・グレー（管理者・閲覧）
    writable: { type: Boolean, default: true },
    // 必須エラー：{ [surveyId]: message }
    errors: { type: Object, default: () => ({}) },
    // 外枠（ContentsBox＝幅1080px・高さ610px固定・枠線）を自前で持つか。
    // 複数回アンケートのように呼び出し側が全回を1つの枠にまとめる場合は false。
    framed: { type: Boolean, default: true },
  },
  data() {
    return {
      TYPE,
      localAnswers: {},
      scrollOption: {
        wheelPropagation: true,
        suppressScrollX: true,
      },
    };
  },
  watch: {
    value: {
      handler() {
        this.localAnswers = this.normalize();
      },
      immediate: true,
    },
    surveys() {
      this.localAnswers = this.normalize();
    },
  },
  methods: {
    normalize() {
      const map = {};
      this.surveys.forEach((s) => {
        const a = (this.value && this.value[s.id]) || {};
        map[s.id] = {
          answer_text: a.answer_text || '',
          option_ids: Array.isArray(a.option_ids) ? a.option_ids.slice() : [],
        };
      });
      return map;
    },
    ensure(id) {
      if (!this.localAnswers[id]) {
        this.$set(this.localAnswers, id, { answer_text: '', option_ids: [] });
      }
    },
    getText(id) {
      return (this.localAnswers[id] && this.localAnswers[id].answer_text) || '';
    },
    setText(id, val) {
      this.ensure(id);
      this.localAnswers[id].answer_text = val;
      this.emit();
    },
    getSingle(id) {
      const o = (this.localAnswers[id] && this.localAnswers[id].option_ids) || [];
      return o.length ? o[0] : null;
    },
    setSingle(id, val) {
      this.ensure(id);
      this.localAnswers[id].option_ids = (val === null || val === undefined) ? [] : [val];
      this.emit();
    },
    getMulti(id) {
      return (this.localAnswers[id] && this.localAnswers[id].option_ids) || [];
    },
    setMulti(id, vals) {
      this.ensure(id);
      this.localAnswers[id].option_ids = vals;
      this.emit();
    },
    emit() {
      const out = {};
      Object.keys(this.localAnswers).forEach((k) => {
        out[k] = {
          answer_text: this.localAnswers[k].answer_text,
          option_ids: this.localAnswers[k].option_ids.slice(),
        };
      });
      this.$emit('input', out);
    },
  },
};
</script>

<style scoped>
.survey-list {
  max-width: 1000px;
}
.survey-block {
  border-bottom: 1px dashed #d6d6d6;
}
.survey-question {
  font-size: 1.0rem;
}
.q-text {
  font-weight: bold;
}
.survey-answer {
  margin-top: 6px;
}
.survey-select {
  max-width: 360px;
}
/* ラジオの丸（25px）。グローバルの .custom-control-label::before { border-radius: 0 !important }
   （overwriteBootstrap.css・コメントは「チェックボックスの形」だがセレクタがラジオにも当たる）を打ち消す。
   報告者側では parts/application/Radio.vue の「scoped でない style」が偶然この役目を担っていたが、
   あれは Radio.vue が読み込まれた画面にしか注入されない。CMS のアンケートプレビュー
   （cms/surveys/List.vue）は Survey.vue だけを読むため打ち消しが効かず、ラジオが四角くなっていた。
   他コンポーネントの CSS 漏れに依存しないよう、ここで自己完結させる。 */
.survey-radio >>> .custom-radio .custom-control-label::before {
  border-radius: 50% !important;
  width: 25px;
  height: 25px;
}
.survey-radio >>> .custom-radio .custom-control-label::after {
  width: 25px;
  height: 25px;
}
/* ラジオ：丸を拡大しているため、横の余白を広げ、文字を丸の中央に合わせる */
.survey-radio >>> .custom-control {
  padding-left: 3.1rem;
  min-height: 30px;
}
.survey-radio >>> .custom-control-label {
  padding-top: 4px;
  line-height: 1.5;
}
.survey-radio >>> .custom-control-label::before,
.survey-radio >>> .custom-control-label::after {
  left: -2.3rem;
}
/* チェック：標準サイズ。下にずれないよう上余白は付けず、横の間隔のみ確保 */
.survey-checkbox >>> .custom-control {
  padding-left: 2.0rem;
  min-height: 24px;
}
.survey-checkbox >>> .custom-control-label {
  padding-top: 0;
  line-height: 1.5;
}
</style>
